import * as yup from 'yup'
import {useLxHelperFunctions} from './helpers.js'
import {inject} from 'vue'

export const useValidation = () => {

  const {calculateDays} = useLxHelperFunctions()
  const formsMetaData = inject('$formsMetaData')

  // methods

  /**
   * Check if ik of insurance is valid in dependency to insurance state
   * (insurance and insurance state must be on same object path)
   *
   * @param {Object} value
   * @param {string} value.ik ik of insurance company
   * @param {Object} options
   * @param {Object} options.parent
   * @param {string} options.parent.insurance_state insurance state of patient
   */
  function ikIsValid(value, options) {
    let valid = true

    const regexIks = {
      legal: RegExp(/^10\d{7}$/),
      private: RegExp(/^16\d{7}|950\d{6}$/),
      others: RegExp(/\d{9}$/)
    }

    if (value?.ik && options.parent.insurance_state && regexIks[options.parent.insurance_state]) {
      valid = regexIks[options.parent.insurance_state].test(value.ik)
    }

    return valid
  }

  /**
   * Check characters for day
   *
   * @param {string} value '1,4-7,11'
   */
  function dayValidation(value) {
    if (!value) {
      return true
    }

    // remove all spaces
    value = value.replace(/\s+/g, '')

    // check if last char is not comma
    if (value[value.length - 1] === ',') {
      // invalid value
      return false
    }

    // add comma for regex validation
    value += ','

    // check valid input
    return (/^(([1-9][0-9]?(-[1-9][0-9]?,|,)?)+)$/g).test(value);
  }

  /**
   * Check characters for day with day 0 allowed
   *
   * @param {string} value '0,4-7,11'
   */
  function dayValidationZeroAllowed(value) {
    if (!value) {
      return true
    }

    // remove all spaces
    value = value.replace(/\s+/g, '')

    // check if last char is not comma
    if (value[value.length - 1] === ',') {
      // invalid value
      return false
    }

    // add comma for regex validation
    value += ','

    // check valid input
    return (/^(([0-9][0-9]?(-[0-9][0-9]?,|,)?)+)$/g).test(value);
  }

  /**
   * Check characters for day with negative day allowed
   *
   * @param {string} value '-7,4-7,11'
   */
  function dayValidationNegativeAllowed(value) {
    if (!value) {
      return true
    }

    // remove all spaces
    value = value.replace(/\s+/g, '')

    // check if last char is not comma
    if (value[value.length - 1] === ',') {
      // invalid value
      return false
    }

    if (value[0] === '-') {
      value = value.substring(1)
    }

    // add comma for regex validation
    value += ','

    // check valid input
    return (/^(([0-9][0-9]?(-[0-9][0-9]?,|,)?)+)$/g).test(value);
  }

  /**
   * Check duplication for day
   *
   * @param {string} value '1,4-7,11,11'
   */
  function cycleDuplicationValidation(value) {
    let days = calculateDays(value)
    let temp = days.reduce((prev, cur) => (prev.indexOf(cur) === -1) ? [...prev, cur] : prev, [])
    return days.length === temp.length
  }

  /**
   * Check if range reversed
   *
   * @param {string} value '1,4-7,11'
   */
  function checkRangeReversed(value) {
    if (!value) {
      return true
    }

    let notFound = true

    value = value.replace(/\s+/g, '')
    value.split(',').forEach(item => {
      if (item.indexOf('-') >= 0) {
        let parts = item.split('-')

        if (parts.length === 2) {
          let tmpPart0 = parseInt(parts[0], 10)
          let tmpPart1 = parseInt(parts[1], 10)

          if (isFinite(tmpPart0) && isFinite(tmpPart1)) {
            notFound = tmpPart0 < tmpPart1
          }
        }
      }
    })

    return notFound
  }

  /**
   * Check no spaces at begin and end
   *
   * @param {string} value
   */
  function noSpacesValidation(value) {
    return !(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g).test(value)
  }

  /**
   * Check valid time in value
   *
   * @param {string} value
   * @returns {boolean}
   */
  function checkValidTime(value) {
    if (!value) {
      return true
    }

    const dateTmp = new Date(value)

    if (dateTmp.toString() === 'Invalid Date') {
      return false
    }

    return typeof dateTmp === 'object' && dateTmp instanceof Date
  }

  /**
   * Zip code validation german format
   *
   * @param {string} value
   * @returns {boolean}
   */
  function zipCodeValidation(value) {
    if (value) {
      return (/[0-9]{5}/g).test(value)
    }
    return true
  }

  /**
   * Check if date is not in future
   *
   * @param {string | Date } value
   * @returns {boolean}
   */
  function dateIsNotInFuture(value) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const date = new Date(value)
    date.setHours(0, 0, 0, 0)

    return !value || date <= today
  }


  /**
   * Validation: Check password string complexity
   * */
  function checkPasswordComplexity(password) {

    // Reguläre Ausdrücke zur Überprüfung
    const hasLowerCase = /[a-z]/.test(password); //Kleinbuchstaben
    const hasUpperCase = /[A-Z]/.test(password); //Großbuchstaben
    const hasNumber = /\d/.test(password); //Zahlen
    //const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    // Überprüfen, ob alle Bedingungen erfüllt sind
    return hasLowerCase && hasUpperCase && hasNumber //&& hasSpecialChar;
  }

  /**
   * Validation: helper function for calculateRisk
   * */
  function calculateComperatorMatchPercentage(password, comperator) {
    const lowerComperator = comperator.toLowerCase();
    const lowerPassword = password.toLowerCase();
    let longestMatch = 0;

    // Durchlaufe das Passwort, um den längsten zusammenhängenden Teil des Benutzernamens zu finden
    for (let i = 0; i < lowerPassword.length; i++) {
      for (let j = 0; j < lowerComperator.length; j++) {
        let matchLength = 0;

        // Finde die maximale Länge der Übereinstimmung ab der Position i im Passwort
        while (i + matchLength < lowerPassword.length &&
        j + matchLength < lowerComperator.length &&
        lowerPassword[i + matchLength] === lowerComperator[j + matchLength]) {
          matchLength++;
        }

        // Aktualisiere die längste Übereinstimmung
        if (matchLength > longestMatch) {
          longestMatch = matchLength;
        }
      }
    }

    // Berechne den Prozentwert der längsten Übereinstimmung zum Benutzernamen
    const matchPercentage = (longestMatch / comperator.length) * 100;

    return matchPercentage;
  }

  /**
   * Validation: Check password not same as username or email, or any other given value, calculates the equality in percentage
   * */
  function calculateRisk(password, comperator) {
    if (!comperator || !password || comperator.length < 1 || password.length < 1) {
      return 0
    }

    const lowerComperator = comperator.toLowerCase();
    const lowerPassword = password.toLowerCase();

    const comperatorLength = lowerComperator.length;
    const passwordLength = lowerPassword.length;

    let riskFactor = calculateComperatorMatchPercentage(password, comperator)

    // Berechnung des Gefahrenwerts basierend auf der Passwortlänge
    const lengthRatio = passwordLength / comperatorLength;

    riskFactor -= (50 * (lengthRatio - 1))

    return riskFactor;
  }

  /**
   * Check if date is not in past
   *
   * @param {string | Date } value
   * @returns {boolean}
   */
  function dateIsNotInPast(value) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const date = new Date(value)
    date.setHours(0, 0, 0, 0)

    return !value || date >= today
  }

  /**
   * Check if email exists already
   *
   * @param value
   * @returns { RegExpMatchArray | null}
   */
  function extendedEmailCheck(value) {
    return value.match(formsMetaData.regexes.emailValidator)
  }

  /**
   * Check negative numbers
   *
   * @param value
   * @returns {boolean}
   */
  function negativeDayValidation(value) {
    const strValue = String(value)

    // Positive numbers oder Strings until 50 characters
    if (strValue.length > 0 && strValue.length <= 50 && !strValue.startsWith('-')) {
      return true
    }

    // Negative numbers from -999
    if (strValue.startsWith('-')) {
      const numValue = parseFloat(strValue)
      if (!isNaN(numValue) && numValue >= -999) {
        return true
      }
    }
    return false
  }

  // yup extensions

  /**
   * Check if highest day is <= cycle_length
   */
  yup.addMethod(yup.string, 'lowerEqualCycleLength', function (lengthOfCycle, message) {
    return this.test('lowerEqualCycleLength', message, value => {
      let days = calculateDays(value)
      if (days.length > 0) {
        return days[days.length - 1] <= lengthOfCycle
      }
      return true
    })
  })

  /**
   * Check volume of carrier solution
   *
   * @param carrier_solution
   * @param message
   * @returns {boolean}
   */
  yup.addMethod(yup.number, 'checkVolumeOfCarrierSolution', function (carrier_solution, message) {
    return this.test('checkVolumeOfCarrierSolution', message, (value) => {
      // if carrier_solution is existing and has the name 'unverdünnt' then value must be 0
      if (carrier_solution?.name !== formsMetaData.constants.undiluted) {
        return value && value !== 0
      } else {
        return true
      }
    })
  })

  /**
   * Check if passwords are equal
   *
   * @param password
   * @param message
   * @returns {boolean}
   */
  yup.addMethod(yup.string, 'passwordEqualityValidation', function (password, message) {
    return this.test(
      'password-equality-validation',
      message,
      (value) => {
        if (value && password) {
          return ((value === password) || (value && !password) || (!value && password))
        }
        return true
      })
  })

  /**
   * Test when agent is biosimilar then manufacturer is required
   */
  yup.addMethod(yup.string, 'manufacturerRequiredIfAgentIsBiosimilar', function (dataModel, message) {
    return this.test('manufacturerRequiredIfAgentIsBiosimilar', message, (value, context) => {
      let isNotRequired = true

      // path -> 'cycle_therapies[4].cycle_agents[0].manufacturer.Langname'
      let arr = context.path.split('[')

      if (arr.length === 3) {
        let arr2 = arr[1].split(']')
        let i = parseInt(arr2[0])

        let arr3 = arr[2].split(']')
        let j = parseInt(arr3[0])

        let cycleTherapy = dataModel[i]
        let cycleAgent = cycleTherapy.cycle_agents[j]

        if (cycleTherapy.selected && cycleAgent.tmpDetails.isBiosimilar && !cycleAgent.manufacturer?.Langname) {
          isNotRequired = false
        }
      }

      return isNotRequired
    })
  })

  /**
   * Test when schema name has carboplatin then creatinine is required
   */
  yup.addMethod(yup.number, 'creatinineRequiredIfCarboplatin', function (schemaName, carboplatinRequired, message) {
    return this.test('requiredIf2', message, (value) => {
      if (schemaName && schemaName?.indexOf('Carboplatin') >= 0) {
        return Boolean(value)
      } else {
        return !carboplatinRequired
      }
    })
  })

  /**
   * Check if volume validation in range or value is greater or equal to min volume
   *
   * @param total_volume_range
   * @param messageSingle
   * @param messageRange
   * @returns {boolean}
   */
  yup.addMethod(yup.number, 'checkVolumeValidation', function (total_volume_range, messageSingle, messageRange) {
    return this.test(
      'check-volume-range-validation',
      () => {
        //if max and max is type of number
        if (total_volume_range.max && typeof total_volume_range.max === 'number') {
          return messageRange
        }
        return messageSingle
      },
      (value) => {
        // if value and total_volume_range exists
        if (value && total_volume_range) {
          // if min or min is 0 and max exists
          if ((total_volume_range.min || total_volume_range.min === 0) && total_volume_range.max) {
            // return true if value is greater or equal to min and lower or equal to max
            return value >= total_volume_range.min && value <= total_volume_range.max
            // if min or min is 0, no max or type of max is not a number
          } else if ((total_volume_range.min || total_volume_range.min === 0) && (!total_volume_range.max || typeof total_volume_range.max !== 'number')) {
            // return true if value is greater or equal to min
            return value >= total_volume_range.min
          }
        }
        return true
      }
    )
  })

  return {
    checkValidTime,
    dateIsNotInFuture,
    dateIsNotInPast,
    dayValidation,
    dayValidationZeroAllowed,
    dayValidationNegativeAllowed,
    ikIsValid,
    cycleDuplicationValidation,
    noSpacesValidation,
    checkRangeReversed,
    zipCodeValidation,
    calculateRisk,
    checkPasswordComplexity,
    extendedEmailCheck,
    negativeDayValidation
  }
}
